import React, {useCallback, useEffect, useLayoutEffect} from 'react';

import {useSelector} from "react-redux";
import users from "../../../users";
import {cn, getLocaleDateString, isHtmlEmpty} from "../../../../utils/utils";
import ButtonV2 from "../../../shared/button-v2/button-v2";
import ReactQuill from "react-quill";
import {toast} from "react-toastify";

import 'react-quill/dist/quill.snow.css';
import 'react-quill/dist/quill.bubble.css';
import './tmdb-review-form.scss';
import {useLocation, useNavigate} from "react-router-dom";
import auth from "../../../../utils/authentication";
import {SquadflixReviewsApi} from "../../../../apis/squadflix-reviews/SquadflixReviewsApi";
import {CreateItemReviewRequest, ItemReviewDTO} from "../../../../apis/chatbot/api-types";

const modules = {
    toolbar: [
        [{'header': [1, 2, false]}],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{'list': 'ordered'}, {'list': 'bullet'}, {'indent': '-1'}, {'indent': '+1'}],
        ['link'],
        ['clean']
    ],
};

const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'link',
];

type Props = {
    tmdbItem: any;
    season?: number;
    episode?: number;
    isMovie: boolean;
    handleOpenWriteReview: () => void;
    isOpenWriteReview: boolean;
}

const TmdbReviewForm = ({
                            tmdbItem,
                            season,
                            episode,
                            isMovie,
                            handleOpenWriteReview,
                            isOpenWriteReview
                        }: Props) => {

    const location = useLocation();
    const navigate = useNavigate();
    const [like, setLike] = React.useState<boolean | null>(null);
    const [reviewText, setReviewText] = React.useState<string>('');
    const [watchDate, setWatchDate] = React.useState<string>('');
    const [previouslyWatched, setPreviouslyWatched] = React.useState<boolean>(false);
    const [containsSpoilers, setContainsSpoilers] = React.useState<boolean>(false);
    const [loading, setLoading] = React.useState<boolean>(false);
    const [tags, setTags] = React.useState<string[]>([]);
    const [reviewRate, setReviewRate] = React.useState<number>(0);
    const darkMode = useSelector(users.selectors.isDarkMode);
    const [previousReviews, setPreviousReviews] = React.useState<ItemReviewDTO[]>([]);

    const restartReviewForm = useCallback(() => {
        setLike(null);
        setReviewText('');
        setWatchDate((new Date()).toISOString());
        setContainsSpoilers(false);
        setLoading(false);
        setTags([]);
        setReviewRate(0);

        // get reviews for this item
        SquadflixReviewsApi.find({
            uid: auth.currentUser.uid,
            tmdb_id: tmdbItem.serie?.id ?? tmdbItem.id,
            is_movie: isMovie ?? !!tmdbItem?.is_movie,
            season: season > -1 ? season : undefined,
            episode: episode > -1 ? episode : undefined,
        }).then(res => {
            const filteredResults = res.results.filter(r => {
                if (season > -1 && episode > -1) {
                    return r.season == season && r.episode == episode;
                } else if (season == -1) {
                    return r.season == undefined;
                } else {
                    return true;
                }
            });
            setPreviousReviews(filteredResults);
            setPreviouslyWatched(filteredResults.length > 0);
        }).catch(() => {
            // do nothing
        });
    }, [episode, isMovie, season, tmdbItem]);

    useEffect(() => {
        restartReviewForm();
    }, [restartReviewForm]);

    const handleStarsClick = useCallback((e) => {
        if (e.pressure === 0) return;
        e.preventDefault();
        const rect = e.currentTarget.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const starWidth = rect.width / 10;
        const newRate = Math.round(x / starWidth);
        setReviewRate(Math.min(10, newRate));
    }, []);

    const handleStarsTouch = useCallback((e) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const touch = e.touches[0];
        const x = touch.clientX - rect.left;
        const starWidth = rect.width / 10;
        const newRate = Math.round(x / starWidth);
        setReviewRate(Math.min(10, newRate));
    }, []);

    useLayoutEffect(() => {
        if (!isOpenWriteReview) return;
        const quillEditor = document.querySelector('.tmdb-review-form--writing-review .ql-editor');
        if (quillEditor) {
            (quillEditor as any).focus();
        }
    }, [isOpenWriteReview]);

    const handleSubmitReview = () => {
        if (!reviewRate) {
            toast.error(<>Por favor, asigna una puntuación (<i className="fa-solid fa-star text-warning fa-sm"
                                                               draggable={false}></i>) antes de enviar la reseña.</>);
            return;
        }

        const reviewPayload: CreateItemReviewRequest = {
            uid: auth.currentUser.uid,
            tmdb_id: tmdbItem.serie?.id ?? tmdbItem.id,
            is_movie: isMovie ?? !!tmdbItem?.is_movie,
            season: season > -1 ? season : undefined,
            episode: episode > -1 ? episode : undefined,
            review: isHtmlEmpty(reviewText) ? null : reviewText,
            like: like,
            vote: reviewRate > 0 ? reviewRate : null,
            first_time_watch: !previouslyWatched,
            contains_spoilers: containsSpoilers,
            review_date: watchDate ? new Date(watchDate) : null,
        }

        setLoading(true);
        SquadflixReviewsApi.submitReview(reviewPayload).then(res => {
            toast.success("Reseña guardada correctamente.");
            restartReviewForm();
            navigate(-1);
        }).catch(() => {
            toast.error("Error al enviar la reseña. Por favor, inténtalo de nuevo más tarde.");
        }).finally(() => {
            setLoading(false);
        });
    }

    useEffect(() => {
        if (isOpenWriteReview) return;
        if (isHtmlEmpty(reviewText)) {
            setContainsSpoilers(false);
        }

    }, [isOpenWriteReview, reviewText]);

    if (!tmdbItem) return null;

    if (isOpenWriteReview) {
        return <div className={cn("tmdb-review-form--writing-review", {"tmdb-review-form--dark": darkMode})}
                    style={{
                        height: '520px',
                    }}
                    draggable={false}>
            <div className="tmdb-review-form--submit-section justify-content-start pb-2">
                <ButtonV2 variant="secondary" size={"small"} onClick={() => navigate(-1)}>
                    <i className="fa-solid fa-angle-left pe-2"></i> Volver
                </ButtonV2>
            </div>
            <ReactQuill theme="snow"
                        preserveWhitespace={false}
                        style={{
                            height: "calc(100% - 70px)",
                        }}
                        value={reviewText} modules={modules} formats={formats}
                        onChange={setReviewText}/>
        </div>;
    }

    return <div className={cn("tmdb-review-form", {"tmdb-review-form--dark": darkMode})} draggable={false}>
        <div className="tmdb-review-form--date-section">
            {!watchDate &&
                <div className="tmdb-review-form--date-section-input" onClick={() => {
                    const input = document.getElementById('watch-date-input') as HTMLInputElement;
                    console.log(input);
                    if (input) {
                        input.showPicker();
                    }
                }}>
                    <i className="fa-solid fa-calendar pe-1"></i>
                    <span>Añadir fecha...</span>
                    <input placeholder=""
                           type="date" value={watchDate} id="watch-date-input"
                           onChange={(e) => setWatchDate(e.target.value)} draggable={false}/>
                </div>}
            {watchDate &&
                <>
                    <div className="tmdb-review-form--date-section-label">
                        <i className="fa-solid fa-calendar pe-1"></i>
                        <span>{getLocaleDateString(watchDate)}</span>
                    </div>
                    <ButtonV2 width="40" size="small" style={{
                        padding: '0px',
                    }} onClick={() => setWatchDate(null)}>
                        <i className="fa-solid fa-delete-left"></i>
                    </ButtonV2>
                </>}
        </div>
        <div className="tmdb-review-form--rate-section" draggable={false}>
            <div className="tmdb-review-form--rate-section-stars" onPointerDown={handleStarsClick}
                 onPointerMove={handleStarsClick}
                 onTouchMove={handleStarsTouch}
                 draggable={false}>
                {
                    Array.from({length: 5}).map((_, index) => {
                        return <i key={index} className="fa-solid fa-star" draggable={false}></i>;
                    })
                }
                <div className="tmdb-review-form--rate-section-stars-overlay" draggable={false}
                     style={{width: `${(reviewRate / 10) * 100}%`}}>
                    {
                        Array.from({length: 5}).map((_, index) => {
                            return <i key={index} className="fa-solid fa-star text-warning" draggable={false}></i>;
                        })
                    }
                </div>
            </div>
            <div className="tmdb-review-form--rate-section-heart" draggable={false} onClick={() => {
                setLike(!like);
            }}>
                <i className={cn("fa-solid fa-heart", {"text-danger": like})}></i>
            </div>
        </div>
        <div className="tmdb-review-form--review-textarea"
             onClick={() => handleOpenWriteReview()}>
            {!isHtmlEmpty(reviewText) ?
                <span dangerouslySetInnerHTML={{__html: reviewText}}></span> : "Añade una reseña..."}
        </div>
        <div className="tmdb-review-form--review-extra-info">
            <button onClick={() => setPreviouslyWatched(!previouslyWatched)}
                    className={cn("tmdb-review-form--review-extra-info-option", {"tmdb-review-form--review-extra-info-option--active": previouslyWatched})}>
                <div className="position-relative">
                    <i className="fa-solid fa-eye main-icon"></i>
                    {previouslyWatched && <div className="tmdb-review-form--review-extra-info-option-checkmark">
                        <i className="fa-solid fa-rotate"></i>
                    </div>}
                </div>
                {!previouslyWatched && <span>Primera vez</span>}
                {previouslyWatched && <span>Ya lo tengo visto antes</span>}
            </button>
            <button onClick={() => {
                if (isHtmlEmpty(reviewText)) {
                    setContainsSpoilers(false);
                    return;
                }

                setContainsSpoilers(!containsSpoilers);
            }}
                    className={cn("tmdb-review-form--review-extra-info-option", {"tmdb-review-form--review-extra-info-option--active": containsSpoilers})}>
                <div className="position-relative">
                    <i className="fa-solid fa-ghost main-icon"></i>
                    {containsSpoilers && <div className="tmdb-review-form--review-extra-info-option-checkmark">
                        <i className="fa-solid fa-circle-exclamation"></i>
                    </div>}
                </div>
                {!containsSpoilers && <span>Sin spoilers</span>}
                {containsSpoilers && <span>Contiene spoilers</span>}
            </button>
        </div>
        <div className="tmdb-review-form--submit-section">
            <ButtonV2 variant="primary" onClick={handleSubmitReview}>
                {loading && <i className="fa-solid fa-spinner fa-spin me-2"></i>}
                {!previousReviews.length && "Guardar reseña"}
                {!!previousReviews.length && "Guardar otra reseña"}
            </ButtonV2>
        </div>
    </div>;
}

export default TmdbReviewForm;
